import React, { useCallback, useEffect, useState } from 'react';
import {
  getDashboardConfig, putDashboardConfig, resetDashboardConfig,
  type DashboardConfig, type DashboardConfigResponse, type DashboardTab, type DashboardWidget,
} from '../../../api/workspaceConfigApi';

/** Customer dashboard customisation (SRS #124): what the team sees, within the plan. */
const DashboardConfigPanel: React.FC = () => {
  const [data, setData] = useState<DashboardConfigResponse | null>(null);
  const [draft, setDraft] = useState<DashboardConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getDashboardConfig(true).then((r) => { setData(r); setDraft(r.stored); }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data || !draft) return null;
  if (!data.can_edit) return null;

  const border = 'var(--ds-border, var(--ds-border))';
  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const text = 'var(--color-text, var(--ds-text))';
  const input: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: `1px solid ${border}`, fontSize: 13, background: 'var(--ds-surface, #fff)', color: text, width: '100%', boxSizing: 'border-box' };
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`, background: 'var(--ds-surface, #fff)', cursor: 'pointer', fontSize: 13, color: text };

  const move = (tab: DashboardTab, dir: -1 | 1) => {
    const i = draft.tabs.indexOf(tab); const j = i + dir;
    if (i < 0 || j < 0 || j >= draft.tabs.length) return;
    const tabs = [...draft.tabs]; [tabs[i], tabs[j]] = [tabs[j], tabs[i]];
    setDraft({ ...draft, tabs });
  };
  const toggleTab = (tab: DashboardTab, on: boolean) => {
    const tabs = on ? [...draft.tabs, tab] : draft.tabs.filter((t) => t !== tab);
    setDraft({ ...draft, tabs, landing_tab: tabs.includes(draft.landing_tab) ? draft.landing_tab : 'dashboard' });
  };
  const save = async () => {
    setBusy(true); setError(null); setOk(null);
    try { await putDashboardConfig(draft); setOk('Saved — the team sees it on their next page load.'); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm('Reset the dashboard to the defaults for everyone in the workspace?')) return;
    setBusy(true); setError(null); setOk(null);
    try { await resetDashboardConfig(); setOk('Reset.'); load(); } catch (e) { setError(e instanceof Error ? e.message : 'Reset failed'); } finally { setBusy(false); }
  };

  return (
    <section style={{ border: `1px solid ${border}`, borderRadius: 14, padding: 18, background: 'var(--ds-surface, #fff)', marginTop: 18 }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Dashboard for this workspace</div>
      <div style={{ fontSize: 12.5, color: muted, marginBottom: 12 }}>Choose what your team sees when they open PolicyOra. Items your plan does not include are shown greyed out; upgrading reveals them without reconfiguring.</div>
      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {ok && <div style={{ color: 'var(--ds-success-text)', fontSize: 13, marginBottom: 8 }}>{ok}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: text }}>Sections and order</div>
          {data.permitted.tabs.map((t) => {
            const on = draft.tabs.includes(t.key);
            const idx = draft.tabs.indexOf(t.key);
            return (
              <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', opacity: t.allowed ? 1 : 0.5, fontSize: 13, color: text }}>
                <input type="checkbox" checked={on} disabled={!t.allowed || t.key === 'dashboard'} onChange={(e) => toggleTab(t.key, e.target.checked)} />
                <span style={{ flex: 1 }}>{t.label}{!t.allowed && <span style={{ color: muted, fontSize: 11 }}> · needs {t.requires?.replace(/_/g, ' ')}</span>}</span>
                {on && <><button type="button" style={{ ...btn, padding: '1px 6px' }} disabled={idx <= 0} onClick={() => move(t.key, -1)} aria-label="Move up">↑</button>
                  <button type="button" style={{ ...btn, padding: '1px 6px' }} disabled={idx >= draft.tabs.length - 1} onClick={() => move(t.key, 1)} aria-label="Move down">↓</button></>}
              </div>
            );
          })}
          <label style={{ display: 'block', fontSize: 13, marginTop: 8, color: text }}>Open on
            <select style={{ ...input, marginTop: 4 }} value={draft.landing_tab} onChange={(e) => setDraft({ ...draft, landing_tab: e.target.value as DashboardTab })}>
              {draft.tabs.map((t) => <option key={t} value={t}>{data.permitted.tabs.find((x) => x.key === t)?.label || t}</option>)}
            </select>
          </label>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: text }}>Overview widgets</div>
          {data.permitted.widgets.map((w) => (
            <label key={w.key} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0', fontSize: 13, opacity: w.allowed ? 1 : 0.5, color: text }}>
              <input type="checkbox" checked={!!draft.widgets[w.key]} disabled={!w.allowed} onChange={(e) => setDraft({ ...draft, widgets: { ...draft.widgets, [w.key as DashboardWidget]: e.target.checked } })} />
              {w.label}{!w.allowed && <span style={{ color: muted, fontSize: 11 }}> · needs {w.requires?.replace(/_/g, ' ')}</span>}
            </label>
          ))}
          <label style={{ display: 'block', fontSize: 13, marginTop: 8, color: text }}>Default period
            <select style={{ ...input, marginTop: 4 }} value={draft.default_filters.date_range} onChange={(e) => setDraft({ ...draft, default_filters: { ...draft.default_filters, date_range: e.target.value as DashboardConfig['default_filters']['date_range'] } })}>
              {data.permitted.date_ranges.map((d) => <option key={d} value={d}>{d === 'all' ? 'All time' : `Last ${d.replace('d', ' days').replace('y', ' year')}`}</option>)}
            </select>
          </label>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, color: text }}>Workspace name
            <input style={{ ...input, marginTop: 4 }} maxLength={80} value={draft.workspace_name ?? ''} placeholder="Defaults to your organisation" onChange={(e) => setDraft({ ...draft, workspace_name: e.target.value || null })} />
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8, color: text }}>Note shown at the top of the dashboard
            <textarea style={{ ...input, marginTop: 4, minHeight: 60, font: 'inherit' }} maxLength={300} value={draft.welcome_note ?? ''} placeholder="e.g. Q4 focus: EU AI Act readiness — see the pinned items" onChange={(e) => setDraft({ ...draft, welcome_note: e.target.value || null })} />
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8, color: text }}>Accent colour
            <input type="color" style={{ marginLeft: 8, verticalAlign: 'middle' }} value={draft.accent ?? 'var(--ds-brand-blue)'} onChange={(e) => setDraft({ ...draft, accent: e.target.value })} />
            {draft.accent && <button type="button" style={{ ...btn, marginLeft: 8, padding: '2px 8px', fontSize: 12 }} onClick={() => setDraft({ ...draft, accent: null })}>default</button>}
          </label>
          <label style={{ display: 'block', fontSize: 13, marginTop: 8, color: text }}>Default jurisdictions (comma-separated)
            <input style={{ ...input, marginTop: 4 }} value={draft.default_filters.jurisdictions.join(', ')} placeholder="European Union, United Kingdom" onChange={(e) => setDraft({ ...draft, default_filters: { ...draft.default_filters, jurisdictions: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) } })} />
          </label>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" style={{ ...btn, background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', border: 'none', fontWeight: 600 }} disabled={busy} onClick={save}>Save for the team</button>
        <button type="button" style={btn} disabled={busy} onClick={reset}>Reset to defaults</button>
      </div>
    </section>
  );
};

export default DashboardConfigPanel;
